import BasePage from '../base-page';

class ProductSection extends HTMLElement {
        static get observedAttributes() {
                return ['source', 'source-value', 'layout', 'limit', 'container'];
        }

        constructor() {
                super();
                this.products = [];
                this.loading = false;
                this.error = null;

                this.source = 'latest';
                this.sourceValue = [];
                this.layout = 'slider';
                this.container = false;
                this.limit = null;

                this._inited = false;
                this._io = null;
                this._attrChangeTimer = null;
                this._inflight = null;
                this._abort = null;
                this._retryCount = 0;
                this._maxRetries = 2;
                this._timeoutMs = 12000;

                this._appReady = false;
                this._readyWaiter = null;
        }

        
        waitForAppReady() {
                if (this._appReady || (window.app?.status === 'ready')) {
                        this._appReady = true;
                        return Promise.resolve();
                }
                
                if (this._readyWaiter) return this._readyWaiter;

                this._readyWaiter = new Promise((resolve) => {
                        const onReady = () => {
                                this._appReady = true;
                                resolve();
                        };
                        
                        document.addEventListener('theme::ready', onReady, { once: true });

                        
                        setTimeout(() => {
                                if (!this._appReady) onReady();
                        }, 10000);
                });

                return this._readyWaiter;
        }

        connectedCallback() {
                this._readAttributes();
                
                this.waitForAppReady().then(() => {
                        this._setupLazyInit();
                });

                
                if (!this.innerHTML.trim()) this._renderSkeleton();
        }

        disconnectedCallback() {
                if (this._io) this._io.disconnect();
                this._cancelInFlight();
                if (this._attrChangeTimer) clearTimeout(this._attrChangeTimer);
        }

        attributeChangedCallback(name, _old, _val) {
                this._readAttributes();

                if (['layout'].includes(name)) {
                        this.render();
                        return;
                }

                if (['source', 'source-value', 'limit', 'container'].includes(name)) {
                        if (this._attrChangeTimer) clearTimeout(this._attrChangeTimer);
                        this._attrChangeTimer = setTimeout(() => {
                                
                                this.waitForAppReady().then(() => {
                                        if (this._inited) this._safeInit();
                                });
                        }, 150);
                }
        }

        _setupLazyInit() {
                
                if ('IntersectionObserver' in window) {
                        this._io = new IntersectionObserver((entries) => {
                                const vis = entries.some(e => e.isIntersecting);
                                if (vis && !this._inited) {
                                        this._inited = true;
                                        this._safeInit();
                                        this._io.disconnect();
                                }
                        }, { rootMargin: '200px' });
                        this._io.observe(this);
                } else {
                        this._inited = true;
                        this._safeInit();
                }
        }

        _safeInit() {
                this._cancelInFlight();
                this.error = null;
                this.products = [];
                this._retryCount = 0;
                this.init();
        }

        async init() {
                try {
                        
                        await this.waitForAppReady();
                        await this.fetchProductsWithRetry();
                        this.render();
                } catch (err) {
                        console.error('[INIT_ERROR]', err);
                        this._renderError(this._t('pages.products.load_error', 'An error occurred while loading'));
                }
        }

        _readAttributes() {
                this.source = this.getAttribute('source') || 'latest';

                try {
                        const raw = this.getAttribute('source-value');
                        const parsed = raw ? JSON.parse(raw) : [];
                        this.sourceValue = Array.isArray(parsed) ? parsed : [];
                } catch {
                        this.sourceValue = [];
                }

                this.layout = this.getAttribute('layout') || 'slider';
                this.container = this.getAttribute('container');

                const limitAttr = Number.parseInt(this.getAttribute('limit'), 10);
                this.limit = Number.isFinite(limitAttr) && limitAttr > 0 ? limitAttr : null;
        }

        _cancelInFlight() {
                try { this._abort?.abort(); } catch { /* ignore */ }
                this._abort = null;
                this._inflight = null;
        }

        async fetchProductsWithRetry() {
                while (true) {
                        try {
                                await this.fetchProducts();
                                return;
                        } catch (err) {
                                if (this._retryCount >= this._maxRetries) throw err;
                                this._retryCount++;
                                const backoff = 300 * Math.pow(2, this._retryCount);
                                await new Promise(r => setTimeout(r, backoff));
                        }
                }
        }

        async fetchProducts() {
                if (!window?.salla?.product?.fetch || typeof salla.product.fetch !== 'function') {
                        throw new Error('salla.product.fetch is not available');
                }

                this.setLoading(true);
                this.error = null;

                const queryParams = { source: this.source };
                if (Number.isFinite(this.limit)) queryParams.limit = this.limit;
                if (['categories', 'selected', 'tags'].includes(this.source) && this.sourceValue?.length) {
                        queryParams.source_value = this.sourceValue;
                }

                this._abort = new AbortController();
                const { signal } = this._abort;

                const timeoutPromise = new Promise((_, rej) =>
                        setTimeout(() => rej(new Error('REQUEST_TIMEOUT')), this._timeoutMs)
                );

                const requestPromise = (async () => {
                        const response = await salla.product.fetch(queryParams, { signal });
                        const data = Array.isArray(response?.data) ? response.data : [];
                        this.products = data.filter(p => p && typeof p === 'object' && ('id' in p));
                })();

                this._inflight = Promise.race([requestPromise, timeoutPromise]);

                try {
                        await this._inflight;
                } finally {
                        this.setLoading(false);
                        this._inflight = null;
                        this._abort = null;
                }

                if (!this.products.length) {
                        this._renderEmpty();
                }
        }

        setLoading(state) {
                this.loading = !!state;

                if (this.loading) {
                        this.classList.add('is-loading');
                        this.innerHTML = `
        <div class="py-6 flex flex-col items-center gap-3" role="status" aria-live="polite" aria-busy="true">
          <span class="s-button-loader w-6 h-6 rounded-full"></span>
          <span class="text-sm opacity-80">${this._t('pages.products.loading', 'Loading...')}</span>
        </div>
        ${this._skeletonMarkup()}
      `;
                } else {
                        this.classList.remove('is-loading');
                }
        }

        _skeletonMarkup() {
                const cols = this.layout === 'grid' ? 8 : 5;
                const items = Array.from({ length: cols })
                        .map(() => `
        <div class="animate-pulse">
          <div class="w-full aspect-[3/4] bg-gray-200 rounded-lg mb-3"></div>
          <div class="h-3 bg-gray-200 rounded mb-2"></div>
          <div class="h-3 bg-gray-200 rounded w-2/3"></div>
        </div>
      `).join('');

                if (this.layout === 'grid') {
                        return `<div class="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4" aria-hidden="true">${items}</div>`;
                }
                return `
      <div class="mt-4" aria-hidden="true">
        <div class="grid grid-flow-col auto-cols-[45%] md:auto-cols-[25%] gap-3 overflow-hidden">${items}</div>
      </div>
    `;
        }

        render() {
                if (this.loading) return;
                if (this.error) {
                        this._renderError(this.error);
                        return;
                }

                if (!this.products.length) {
                        this._renderEmpty();
                        return;
                }

                const content = this.layout === 'grid' ? this.renderGrid() : this.renderSlider();
                this.innerHTML = content;
        }

        renderSlider() {
                const sliderConfig = {

                        centerInsufficientSlides: true,
                        breakpoints: {
                                0: { slidesPerView: 1.75 },
                                768: { spaceBetween: 5, slidesPerView: 3 },
                                1024: { slidesPerView: this.container ? 3 : 4 },
                                1280: { slidesPerView: this.container ? 3 : 5 },
                        }
                };

                const items = this.products.map(product => `
      <div class="!h-auto">
        <custom-salla-product-card product='${this.safeJson(product)}'></custom-salla-product-card>
      </div>
    `).join('');

                return `
      <salla-slider show-controls="false" type="carousel" slider-config='${this.escapeHtml(JSON.stringify(sliderConfig))}'>
        <div slot="items">
          ${items}
        </div>
      </salla-slider>
    `;
        }

        renderGrid() {
                const items = this.products.map(product => `
      <div class="!h-auto">
        <custom-salla-product-card product='${this.safeJson(product)}'></custom-salla-product-card>
      </div>
    `).join('');

                return `
      <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
        ${items}
      </div>
    `;
        }



        _renderEmpty() {
                this.innerHTML = `
      <p class="py-6 text-center" role="status" aria-live="polite">
        ${this.escapeHtml(this._t('pages.products.empty', 'No products to show'))}
      </p>
    `;
        }

        _renderSkeleton() {
                this.setLoading(true);
        }

        _renderError(text) {
                this.error = String(text || this._t('pages.products.load_error', 'An error occurred while loading'));
                this.innerHTML = `<p class="py-6 text-center" role="alert">${this.escapeHtml(this.error)}</p>`;
        }

        
        safeJson(obj) {
                try { return JSON.stringify(obj).replace(/</g, '\\u003c'); }
                catch { return '{}'; }
        }

        escapeHtml(str) {
                return String(str)
                        .replace(/&/g, '&amp;')
                        .replace(/</g, '&lt;')
                        .replace(/>/g, '&gt;')
                        .replace(/"/g, '&quot;')
                        .replace(/'/g, '&#039;');
        }

        _t(key, fallback) {
                try { return (window.salla?.lang?.get?.(key)) || fallback; }
                catch { return fallback; }
        }
}

customElements.get('custom-shahien-products') || customElements.define('custom-shahien-products', ProductSection);